/**
 * RequireAdminOrDemoAdmin — route wrapper that limits a page to
 * authenticated users who are either an admin OR a demo-admin.
 *
 * IJ-259 · 2026-08-05 · Landing the scoped demo-admin role.
 *
 * Purpose:
 *   The Knowledge Control Cockpit route (/knowledge-controls) needs
 *   to be reachable during client demos WITHOUT unlocking every other
 *   admin surface (admin dashboard · admin agents · admin scenarios ·
 *   admin code maintenance · governance tile). This wrapper is used
 *   only on the /knowledge-controls route; every other admin route in
 *   App.js keeps the stricter <RequireAdmin> gate.
 *
 * Behaviour:
 *   • No token → redirect to `/` (public LoginPage).
 *   • Token but /api/auth/verify.is_admin === false AND
 *     is_demo_admin === false → redirect to `/`.
 *   • Token AND (is_admin === true OR is_demo_admin === true) →
 *     render children.
 *   • While the check is inflight, render a minimal "Verifying access…"
 *     placeholder so the page doesn't flash the gated surface.
 *
 * Mirrors the shape of RequireAdmin.jsx byte-for-byte with one
 * semantic difference: the status check accepts either flag.
 */
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { API, getToken } from '../utils/authClient';

export function RequireAdminOrDemoAdmin({ children }) {
  // 'checking' | 'allowed' | 'denied'
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const token = getToken();
    if (!token) { setStatus('denied'); return; }

    let cancelled = false;
    fetch(`${API}/auth/verify`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('verify-failed'))))
      .then((data) => {
        if (cancelled) return;
        const allowed = data?.is_admin === true || data?.is_demo_admin === true;
        setStatus(allowed ? 'allowed' : 'denied');
      })
      .catch(() => { if (!cancelled) setStatus('denied'); });

    return () => { cancelled = true; };
  }, []);

  if (status === 'checking') {
    return (
      <div
        data-testid="require-admin-or-demo-admin-checking"
        className="flex min-h-screen items-center justify-center bg-black"
      >
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400/70">
          Verifying access…
        </div>
      </div>
    );
  }

  if (status === 'denied') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default RequireAdminOrDemoAdmin;
